import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Voice typing (dictation) via the browser's built-in Web Speech API —
 * the same engine behind "talk and it becomes text" in Chrome/Edge/Safari.
 * No external service, no API keys; audio is handled by the browser.
 *
 * Usage: press the mic → live transcription streams into the composer as you
 * talk (interim results included, so text appears WHILE speaking); press the
 * mic again → recording stops and the text stays fully editable, then send.
 *
 * Implementation notes:
 *  • continuous + interimResults for live as-you-talk text.
 *  • Chrome ends recognition on its own after silence — while the user hasn't
 *    pressed stop, onend auto-restarts so one press = one session.
 *  • `supported` is false where the API doesn't exist (e.g. Firefox) — the
 *    caller simply hides the button.
 */

type AnySpeechRecognition = any

function getRecognitionCtor(): (new () => AnySpeechRecognition) | null {
  const w = window as any
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

export function useVoiceTyping(
  /** Called on every recognition update: text finalised this session + the live interim tail. */
  onTranscript: (finalText: string, interim: string) => void,
) {
  const [supported] = useState<boolean>(() => !!getRecognitionCtor())
  const [listening, setListening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recRef = useRef<AnySpeechRecognition | null>(null)
  const wantListeningRef = useRef(false)
  const finalRef = useRef('')
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript

  const stop = useCallback(() => {
    wantListeningRef.current = false
    setListening(false)
    try { recRef.current?.stop() } catch { /* already stopped */ }
    recRef.current = null
  }, [])

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor()
    if (!Ctor || wantListeningRef.current) return
    setError(null)
    finalRef.current = ''

    const rec: AnySpeechRecognition = new Ctor()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = navigator.language || 'en-GB'

    rec.onresult = (e: any) => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        const text = r[0]?.transcript || ''
        if (r.isFinal) {
          // Finals accumulate with sensible spacing.
          finalRef.current = (finalRef.current + ' ' + text).replace(/\s+/g, ' ').trimStart()
        } else {
          interim += text
        }
      }
      onTranscriptRef.current(finalRef.current, interim)
    }

    rec.onerror = (e: any) => {
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        setError('Microphone access was blocked — allow it in the browser and try again.')
        wantListeningRef.current = false
        setListening(false)
        recRef.current = null
      }
      // 'no-speech' / 'aborted' etc. fall through — onend handles restarts.
    }

    rec.onend = () => {
      // The engine times out after silence; keep the session alive until the
      // user explicitly presses stop.
      if (wantListeningRef.current) {
        try { rec.start() } catch { /* mid-teardown — ignore */ }
      }
    }

    try {
      rec.start()
      recRef.current = rec
      wantListeningRef.current = true
      setListening(true)
    } catch {
      setError('Could not start voice typing.')
    }
  }, [])

  const toggle = useCallback(() => {
    if (wantListeningRef.current) stop()
    else start()
  }, [start, stop])

  // Never leave the mic running after the composer unmounts.
  useEffect(() => () => { wantListeningRef.current = false; try { recRef.current?.stop() } catch { /* noop */ } }, [])

  return { supported, listening, error, toggle, stop, clearError: () => setError(null) }
}
